/**
 * Create-cohort screen.
 *
 * The step between "we have a client" and "we can enroll students": the
 * cohort carries the access window (a client runs several engagements
 * with different dates, so the window belongs here and nowhere else) and
 * the defaults every member inherits.
 *
 * English-only by design: internal tooling, never a student-facing
 * surface, so it does not participate in lesson-content review.
 */
import { Checkbox, FieldHelp, Input, Notice } from '@speaking-coach/ui'
import { PageHeader } from '../components/page-header'
import { OPERATOR_FORM_PAGE, OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { SubmitButton } from '../common/save-button'
import { ProvisioningFormActions } from '../common/provisioning-form-actions'
import {
  OPERATOR_FIELD_GROUP,
  OPERATOR_FIELDSET_LEGEND,
} from '../common/form-layout'
import { DEFAULT_TROPHY_GOAL } from '../common/product-defaults'
import {
  UnsavedChangesGuard,
  useLeaveAfterSave,
} from '../common/unsaved-changes'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { keysStaleAfterCohortChange, queryKeys } from '../api/query-keys'
import { AccessWindowPanel, formatWindowLength } from '../common/access-window'
import { adminClientsApi } from '../clients/api'
import { adminHrUsersApi } from '../hr_users/api'
import type { HrUserListRow } from '../hr_users/types'
import { useParams } from 'react-router-dom'
import { adminCohortClient, messageForCohortError } from './api'
import {
  AccessEndsField,
  CohortClassificationFields,
  CohortDescriptionField,
  EnrollmentDefaultsFields,
  CohortInternalField,
  CohortTrialField,
  CohortNameField,
  ProductTierField,
  SendScheduleFields,
  StartDateField,
} from './cohort-form-fields'
import {
  CREATE_DEFAULT_SEND_SCHEDULE,
  EMPTY_SEND_SCHEDULE,
  sendScheduleToBody,
} from './cohort-send-schedule'
import { cohortSchema, type CohortForm } from './cohort-schema'

/** Debounce matched to every other search-as-you-type control here. */
const HR_SEARCH_DEBOUNCE_MS = 250
/** One screenful of matches — narrow further rather than scroll. */
const HR_SEARCH_LIMIT = 20

/**
 * Pick the HR users who should see this cohort the moment it exists.
 *
 * This was a checkbox per HR user, rendered from the unbounded `hrUsers`
 * array embedded in the client detail. Cohort size is load-shaped by the
 * avatar-concurrency cap; HR headcount is shaped by nothing, so a flagship
 * tenant with departmental managers got a checkbox wall with no way to
 * narrow it — and paid for the whole directory on every client-detail load
 * besides.
 *
 * Deliberately NOT the section on the cohort DETAIL page, which looks like
 * the same control and is not: that one mutates each link immediately
 * (`PUT`/`DELETE` per row), because the cohort already exists. Here there
 * is nothing to attach to yet, so this collects ids for the create payload.
 * Same shape, different verb.
 */
function HrAccessField({
  clientId,
  selected,
  onChange,
}: {
  clientId: string
  selected: string[]
  onChange: (ids: string[]) => void
}): JSX.Element {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const selectedSet = useMemo(() => new Set(selected), [selected])

  useEffect(() => {
    const handle = setTimeout(() => setSearch(query.trim()), HR_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  const searchParams = {
    clientId,
    search: search || undefined,
    limit: HR_SEARCH_LIMIT,
    offset: 0,
  }
  const results = useQuery({
    queryKey: queryKeys.hrUserList(searchParams),
    queryFn: () => adminHrUsersApi.list(searchParams),
    placeholderData: keepPreviousData,
  })

  // Remember every row the operator has seen, so a selection made three
  // searches ago still renders. No by-id lookup is needed and none is
  // wanted: this is a CREATE form, `hrUserIds` starts empty, and the only
  // way into it is clicking a row that was on screen. (The EDIT surfaces
  // do need one, because their selection arrives from the server.)
  const [known, setKnown] = useState<Record<string, HrUserListRow>>({})
  useEffect(() => {
    const rows = results.data?.hrUsers ?? []
    if (rows.length === 0) return
    setKnown((current) => {
      const next = { ...current }
      for (const hrUser of rows) next[hrUser.userId] = hrUser
      return next
    })
  }, [results.data])

  const chosenRows = selected
    .map((userId) => known[userId])
    .filter((hrUser): hrUser is HrUserListRow => hrUser !== undefined)

  // What the SERVER returned, before the already-selected are filtered
  // out of the "add" list. The truncation question is about the server's
  // cap, so it has to be asked of this number.
  const returnedCount = results.data?.hrUsers.length ?? 0
  const matches = (results.data?.hrUsers ?? []).filter(
    (hrUser) => !selectedSet.has(hrUser.userId),
  )
  const total = results.data?.total ?? 0

  return (
    <fieldset className={OPERATOR_FIELD_GROUP}>
      <legend className={OPERATOR_FIELDSET_LEGEND}>
        <span className="flex items-center gap-1">
          HR access
          <FieldHelp label="initial cohort HR access">
            Selected HR users can see this cohort as soon as it is created.
            Access can be changed later from the cohort page.
          </FieldHelp>
        </span>
      </legend>

      {chosenRows.length > 0 && (
        <ul className="space-y-1" aria-label="Selected HR users">
          {chosenRows.map((hrUser) => (
            <li key={hrUser.userId} className="flex items-center gap-2 text-body">
              <Checkbox
                checked
                aria-label={`Remove ${hrUser.name}`}
                onCheckedChange={() =>
                  onChange(selected.filter((id) => id !== hrUser.userId))
                }
              />
              {hrUser.name} · {hrUser.email}
            </li>
          ))}
        </ul>
      )}

      <label htmlFor="cohort-hr-search" className="text-caption font-medium">
        Add HR access
      </label>
      <Input
        id="cohort-hr-search"
        type="text"
        role="searchbox"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search HR users by name or email..."
        autoComplete="off"
      />

      {results.isError && (
        <p role="alert" className="text-caption text-danger-text">
          HR users could not be searched. The cohort can still be created;
          add access from the cohort afterwards.
        </p>
      )}
      {results.isSuccess && matches.length === 0 && (
        <p className="text-text-secondary text-caption">
          {search === ''
            ? 'No more HR users in this client.'
            : `No HR users match “${search}”.`}
        </p>
      )}
      <ul className="space-y-1">
        {matches.map((hrUser) => (
          <li key={hrUser.userId} className="flex items-center gap-2 text-body">
            <Checkbox
              checked={false}
              aria-label={`Add ${hrUser.name}`}
              onCheckedChange={() => onChange([...selected, hrUser.userId])}
            />
            {hrUser.name} · {hrUser.email}
          </li>
        ))}
      </ul>
      {/* Against the RETURNED row count, not against `matches` — that is
          the returned rows minus the already-selected ones, and adding
          `chosenRows` back does not correct for it, because a selection
          need not be in the current result set at all. Ten unrelated
          selections and 25 matches: the server returns 20, and
          `25 > 20 + 10` is false, so the list silently hides five. The
          server's cap is the only thing this question is about. */}
      {results.isSuccess && total > returnedCount && (
        <p className="text-text-secondary text-caption">
          Showing {returnedCount} of {total} — keep typing to narrow.
        </p>
      )}
    </fieldset>
  )
}

export function CreateCohortPage(): JSX.Element {
  const { clientId = '' } = useParams<{ clientId: string }>()
  // Success navigation goes through `leave` so the unsaved-changes guard is
  // provably stood down first — see `useLeaveAfterSave`.
  const { leaving, leave } = useLeaveAfterSave()
  const queryClient = useQueryClient()
  // Only so the Classification field can say which way "Same as the client"
  // resolves. Ordinarily already in cache — every route that links here has
  // loaded this client — so this is usually a cache read, not a request.
  const clientQuery = useQuery({
    queryKey: queryKeys.client(clientId),
    queryFn: () => adminClientsApi.get(clientId),
    enabled: clientId !== '',
  })
  /**
   * Live validity keeps the create action honest while Zod issue paths route
   * each explanation to its field. React Hook Form still leaves a pristine
   * form quiet; errors appear only after the operator interacts.
   */
  const form = useForm<CohortForm>({
    resolver: zodResolver(cohortSchema()),
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      scheduledStartOn: '',
      accessEndsOn: '',
      defaultTier: 'pro',
      defaultProgramId: '',
      defaultTrophyGoal: String(DEFAULT_TROPHY_GOAL),
      externalPolicyRef: '',
      isTrial: false,
      isInternal: null,
      hrUserIds: [],
      // Relative by default — see CREATE_DEFAULT_SEND_SCHEDULE for why
      // "no schedule" is the unsafe default on a future-start cohort.
      sendMode: CREATE_DEFAULT_SEND_SCHEDULE.mode,
      sendFixedAt: CREATE_DEFAULT_SEND_SCHEDULE.fixedAt,
      sendOffsetDays: CREATE_DEFAULT_SEND_SCHEDULE.offsetDays,
    },
  })
  const { control, handleSubmit, setValue, watch, formState, trigger } = form
  const errors = formState.errors

  // The product-wide value only keeps the field usable while the client is
  // loading. Once its commercial default arrives, that is the cohort's
  // starting value. Follow refreshed client data only while the operator has
  // not edited the cohort goal themselves.
  const appliedClientTrophyDefault = useRef<number | null>(null)
  const trophyGoalEditedByOperator = useRef(false)
  useEffect(() => {
    if (
      clientQuery.data === undefined ||
      trophyGoalEditedByOperator.current ||
      appliedClientTrophyDefault.current ===
        clientQuery.data.contractTrophyTarget
    ) {
      return
    }
    appliedClientTrophyDefault.current = clientQuery.data.contractTrophyTarget
    setValue('defaultTrophyGoal', String(clientQuery.data.contractTrophyTarget), {
      shouldDirty: false,
      shouldValidate: true,
    })
  }, [clientQuery.data, setValue])

  const scheduledStartOn = watch('scheduledStartOn')
  const accessEndsOn = watch('accessEndsOn')
  const sendMode = watch('sendMode')
  const hasStartDate = scheduledStartOn !== ''
  const windowLength = formatWindowLength(scheduledStartOn, accessEndsOn)

  // Clearing the start date invalidates a relative send: the server refuses the
  // pair, because an offset with nothing to be relative to resolves to no send
  // at all and the whole wave silently never goes out. Drop back rather than
  // submit a 422 for a field the operator can no longer see.
  const clearRelativeSendWithoutStart = useCallback(
    (nextStart: string) => {
      if (nextStart !== '' || sendMode !== 'offset') return
      setValue('sendMode', EMPTY_SEND_SCHEDULE.mode, {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('sendFixedAt', EMPTY_SEND_SCHEDULE.fixedAt, {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('sendOffsetDays', EMPTY_SEND_SCHEDULE.offsetDays, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue, sendMode],
  )

  const create = useMutation({
    mutationFn: (values: CohortForm) =>
      adminCohortClient.create(clientId, {
        name: values.name.trim(),
        description:
          (values.description ?? '').trim() === ''
            ? null
            : (values.description ?? '').trim(),
        scheduledStartOn: values.scheduledStartOn,
        accessEndsOn: values.accessEndsOn,
        defaultTier: values.defaultTier,
        defaultProgramId: values.defaultProgramId.trim(),
        defaultTrophyGoal: Number(values.defaultTrophyGoal),
        externalPolicyRef: values.externalPolicyRef.trim() || null,
        isTrial: values.isTrial,
        isInternal: values.isInternal,
        hrUserIds: values.hrUserIds,
        ...sendScheduleToBody({
          mode: values.sendMode,
          fixedAt: values.sendFixedAt,
          offsetDays: values.sendOffsetDays,
        }),
      }),
    onSuccess: (created) => {
      queryClient.setQueryData(
        queryKeys.cohort(clientId, created.cohortId),
        created,
      )
      for (const key of keysStaleAfterCohortChange(clientId)) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
      // Straight to the cohort page — the warnings (if any) live there,
      // and the next thing an operator does is add people to it.
      leave(`/admin/clients/${clientId}/cohorts/${created.cohortId}`)
    },
  })

  const submitting = create.isPending
  const error = create.error !== null ? messageForCohortError(create.error) : null

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_FORM_PAGE}>
        {/* The client's name already appears in the breadcrumb. Keeping the
            title action-shaped avoids saying the tenant twice in one bar. */}
        <PageHeader title="New cohort" />

        <section className="bg-surface-card rounded-token-card p-6">
          <form
            onSubmit={handleSubmit((values) => {
              if (!formState.isValid || create.isPending) return
              create.mutate(values)
            })}
            className="space-y-4"
            aria-label="New cohort"
            noValidate
          >
            {/* `noValidate`: the zod schema is the authority, and native
                constraint validation would pre-empt it — the browser's own
                bubble appears instead of our wording, and in jsdom it blocks
                submission outright so `handleSubmit` never runs. That is what
                made these messages look like a validation bug rather than the
                form never being submitted. The `required` attributes stay for
                assistive tech. */}
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <CohortNameField
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.name?.message ?? null}
                />
              )}
            />
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <CohortDescriptionField
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
              )}
            />
            {/* The shared panel, not a bespoke fieldset: same shape as the
                student pages, because it is the same concept. The badge says
                what makes THESE dates special — members inherit them live. */}
            <AccessWindowPanel
              titleId="cohort-create-program-access-window-title"
              windowLength={windowLength}
              badge="Inherited live by members"
            >
                {/* Every field goes through `Controller`, including these two.
                    They were wired with `watch` + `setValue` instead, which
                    writes the value but never REGISTERS the field — so the
                    error existed in `formState` and no re-render delivered it.
                    The message rendered for the send fields and not for these,
                    which is what made it look like a validation problem rather
                    than a subscription one.

                    Live validity keeps the primary action honest. Required
                    fields stay quiet until the operator changes them; the
                    disabled action is no longer the mechanism for revealing
                    validation. */}
                <Controller
                  control={control}
                  name="scheduledStartOn"
                  render={({ field }) => (
                    <StartDateField
                      value={field.value}
                      onChange={(next) => {
                        field.onChange(next)
                        clearRelativeSendWithoutStart(next)
                        void trigger(['sendFixedAt', 'sendOffsetDays'])
                      }}
                      error={errors.scheduledStartOn?.message ?? null}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="accessEndsOn"
                  render={({ field }) => (
                    <AccessEndsField
                      value={field.value}
                      onChange={(next) => {
                        field.onChange(next)
                        void trigger(['sendFixedAt', 'sendOffsetDays'])
                      }}
                      startDate={scheduledStartOn}
                      error={errors.accessEndsOn?.message ?? null}
                    />
                  )}
                />
            </AccessWindowPanel>
            <SendScheduleFields
              mode={watch('sendMode')}
              fixedAt={watch('sendFixedAt')}
              offsetDays={watch('sendOffsetDays')}
              hasStartDate={hasStartDate}
              startDate={scheduledStartOn}
              accessEndsOn={accessEndsOn}
              error={
                errors.sendFixedAt?.message ??
                errors.sendOffsetDays?.message ??
                null
              }
              onModeChange={(mode) => {
                setValue('sendMode', mode, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
                void trigger(['sendFixedAt', 'sendOffsetDays'])
              }}
              onFixedChange={(fixedAt) =>
                setValue('sendFixedAt', fixedAt, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              onOffsetChange={(offsetDays) =>
                setValue('sendOffsetDays', offsetDays, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
            <Controller
              control={control}
              name="defaultTier"
              render={({ field }) => (
                <ProductTierField value={field.value} onChange={field.onChange} />
              )}
            />
            <EnrollmentDefaultsFields
              programId={watch('defaultProgramId')}
              trophyGoal={watch('defaultTrophyGoal')}
              externalPolicyRef={watch('externalPolicyRef')}
              programIdError={
                errors.defaultProgramId?.message ?? null
              }
              trophyGoalError={errors.defaultTrophyGoal?.message ?? null}
              onProgramChange={(value) =>
                setValue('defaultProgramId', value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              onTrophyGoalChange={(value) => {
                trophyGoalEditedByOperator.current = true
                setValue('defaultTrophyGoal', value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }}
              onExternalPolicyRefChange={(value) =>
                setValue('externalPolicyRef', value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
            <CohortClassificationFields>
              <Controller
                control={control}
                name="isInternal"
                render={({ field }) => (
                  <CohortInternalField
                    value={field.value}
                    onChange={field.onChange}
                    clientIsInternal={clientQuery.data?.isInternal}
                  />
                )}
              />
              <Controller
                control={control}
                name="isTrial"
                render={({ field }) => (
                  <CohortTrialField
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </CohortClassificationFields>
            {/* Gated on the COUNT, not the embedded preview's length: that
                array is capped now, so a client whose HR users all sit past
                the cap must still get the field. */}
            {clientQuery.data && clientQuery.data.hrUserCount > 0 && (
              <Controller
                control={control}
                name="hrUserIds"
                render={({ field }) => (
                  <HrAccessField
                    clientId={clientId}
                    selected={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}

            {error && (
              <Notice variant="danger">
                {error}
              </Notice>
            )}

            {/* `FormBlockers` is gone: every message now has a field path, so
                there is no leftover pile with nowhere to render. */}

            <ProvisioningFormActions
              cancelTo={`/admin/cohorts?clientId=${encodeURIComponent(clientId)}`}
            >
              <SubmitButton
                pending={submitting}
                pendingLabel="Creating…"
                disabled={!formState.isValid}
              >
                Create cohort
              </SubmitButton>
            </ProvisioningFormActions>
          </form>
        </section>

        <UnsavedChangesGuard
          when={formState.isDirty && !leaving}
          name="the new cohort"
        />
      </div>
    </main>
  )
}
